import React from 'react'

import {
  Accordion,
  AlignItems,
  Body2,
  Bold,
  BorderStyle,
  Box,
  Color,
  Display,
  FontFamily,
  Heading2,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Link,
  LinkVariant,
  Text,
} from '@gousto-internal/citrus-react'

import type { RichTextContent } from '../../types/contentstack'

import type { FaqsFields } from './types'

export type FAQsProps = FaqsFields

type RteNode = {
  type?: string
  uid?: string
  text?: string
  attrs?: { url?: string; target?: string; [key: string]: unknown }
  children?: RteNode[]
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

function isLeaf(node: RteNode): node is RteNode & { text: string } {
  return typeof node.text === 'string'
}

function RichTextLeaf({ node }: { node: RteNode & { text: string } }) {
  let content: React.ReactNode = node.text
  if (node.bold) {
    content = <Bold>{content}</Bold>
  }
  if (node.italic) {
    content = <em>{content}</em>
  }
  if (node.underline) {
    content = <u>{content}</u>
  }
  return <>{content}</>
}

function RichTextChildren({ nodes }: { nodes: RteNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        const key = node.uid ?? `node-${index}`
        if (isLeaf(node)) {
          return <RichTextLeaf key={key} node={node} />
        }
        if (node.type === 'a' && node.attrs?.url) {
          return (
            <Link
              key={key}
              variant={LinkVariant.None}
              href={node.attrs.url}
              style={{ color: 'white', textDecorationLine: 'underline' }}
            >
              <RichTextChildren nodes={node.children ?? []} />
            </Link>
          )
        }
        if (node.type === 'ul') {
          return (
            <ul key={key}>
              <RichTextChildren nodes={node.children ?? []} />
            </ul>
          )
        }
        if (node.type === 'ol') {
          return (
            <ol key={key}>
              <RichTextChildren nodes={node.children ?? []} />
            </ol>
          )
        }
        if (node.type === 'li') {
          return (
            <li key={key}>
              <RichTextChildren nodes={node.children ?? []} />
            </li>
          )
        }
        return <RichTextChildren key={key} nodes={node.children ?? []} />
      })}
    </>
  )
}

function RichTextDescription({ description }: { description?: RichTextContent }) {
  if (!description) {
    return null
  }
  const root = description as RteNode
  const blocks = root.children ?? []
  const paragraphs = blocks.filter((block) => (block.children?.length ?? 0) > 0)

  if (paragraphs.length === 0) {
    return null
  }

  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <Body2 key={paragraph.uid ?? `p-${index}`} size={4} color={Color.White}>
          <RichTextChildren nodes={paragraph.children ?? []} />
        </Body2>
      ))}
    </>
  )
}

function renderAccordionHeader(question: string) {
  return function AccordionHeader(isExpanded: boolean) {
    return (
      <Box
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        alignItems={AlignItems.Center}
      >
        <Heading3 color={Color.White} fontFamily={FontFamily.Bold} size={6}>
          {question}
        </Heading3>

        <Text color={Color.White} size={15}>
          <Icon
            variant={IconVariant.Inherit}
            name={isExpanded ? 'chevron_large_up' : 'chevron_large_down'}
          />
        </Text>
      </Box>
    )
  }
}

export function FAQs({ heading, body }: FAQsProps) {
  const items = body ?? []

  return (
    <Box paddingH={[8, 20]} paddingV={16} display={Display.Block} bg={Color.ColdGrey_600}>
      <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
        <Box maxWidth="1033px" width="100%">
          <Box paddingBottom={2}>
            <Heading2 color={Color.White}>{heading}</Heading2>
          </Box>

          {items.map((item, index) => (
            <Box
              key={item.title}
              borderColor={Color.White}
              borderStyle={BorderStyle.Solid}
              borderBottomWidth={0.5}
              paddingV={5}
              width="100%"
            >
              <Accordion id={`faq-${index}`} title={renderAccordionHeader(item.title)}>
                <Box paddingBottom={2}>
                  <RichTextDescription description={item.description} />
                </Box>
              </Accordion>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
